import {
  addSurvey,
  deleteSurvey,
  getSurveys,
  updateSurvey,
} from "../actions/survey.action";
import TableList from "../components/TableList";
import { Survey } from "../models/survey.model";
import { useEffect, useRef, useState } from "react";
import SurveyForm from "./SurveyForm";
import { filterOnKeys } from "../lib/helper";
import { FilterGroup, FilterType } from "../components/TableFilter";
import { FormRefEvent, HeadCell } from "../models/base.model";
import { getQuestions } from "../actions/question.action";
import { Question } from "../models/question.model";
import { Button } from "@mui/material";
import SurveyEventForm from "./SurveyEventForm";
import { addSurveyEvent } from "../actions/survey.event.action";
import { SurveyEvent } from "../models/survey.event.model";
import { Company } from "../models/company.model";
import { getCompanies } from "../actions/company.action";

const headCells: readonly HeadCell[] = [
  {
    id: "name",
    label: "Name",
  },
  {
    id: "id",
    label: "Preview",
    width: 200,
    render: (v, r) => <CreateEventPreview survey={r} />,
  },
];

let cached_companies: Company[] | undefined;

function CreateEventPreview(props: { survey: Survey }) {
  const formRef = useRef<FormRefEvent<SurveyEvent>>(null);
  const { survey } = props;
  return (
    <Button
      variant="text"
      onClick={async () => {
        window.webApp.setTab<SurveyEvent>({
          title: `Survey Event`,
          element: (
            <SurveyEventForm
              defaultValues={{ survey: survey }}
              surveys={[survey]}
              companies={cached_companies}
              ref={formRef}
            />
          ),
          onOk: async () => {
            if (!formRef.current) {
              throw new Error(
                "Form creation error, please refresh or contact us!",
              );
            }
            const value = await formRef.current.onSubmit();
            const res = await addSurveyEvent(value);
            return res;
          },
          show: true,
        });
      }}
    >
      Preview & Create
    </Button>
  );
}

const filters: FilterGroup<Survey>[] = [
  {
    title: "Filter By Date",
    items: [
      {
        name: "createdAt",
        type: FilterType.date_range,
      },
      {
        name: "updatedAt",
        type: FilterType.date_range,
      },
    ],
  },
];

export default function Surveys() {
  const formRef = useRef<FormRefEvent<Survey>>(null);
  const [questions, setQuestions] = useState<Question[]>();

  useEffect(() => {
    loadQuestions();
    if (!cached_companies) {
      getCompanies().then(({ data }) => (cached_companies = data));
    }
    return () => (cached_companies = undefined);
  }, []);

  const loadQuestions = () =>
    getQuestions().then(({ data }) => setQuestions(data));

  const onAddOrUpdate = async (param?: Survey) => {
    if (!questions) {
      await loadQuestions();
    }
    return window.webApp.setTab<Survey>({
      title: `${param ? "Update" : "New"} Survey`,
      element: (
        <SurveyForm
          questions={questions || []}
          ref={formRef}
          defaultValues={param}
        />
      ),
      onOk: async () => {
        if (!formRef.current) {
          throw new Error("Form creation error, please refresh or contact us!");
        }
        const value = await formRef.current.onSubmit();
        const res = param
          ? await updateSurvey({ ...param, ...value })
          : await addSurvey(value);
        return res;
      },
      show: true,
    });
  };

  const onDelete = (param: Survey) => {
    return deleteSurvey(param.id);
  };

  const onSearch = async (value: string, original: Survey[]) => {
    if (original) {
      return { data: original.filter((a) => filterOnKeys(a, ["name"], value)) };
    } else {
      return { data: [] };
    }
  };

  return (
    <TableList<Survey>
      headCells={headCells}
      onAddBtnText="New Survey"
      uid="id"
      onAdd={onAddOrUpdate}
      onSearch={onSearch}
      searchText="Search by name"
      onEdit={onAddOrUpdate}
      onDelete={onDelete}
      onLoad={getSurveys}
      filters={filters}
    />
  );
}
